import { create } from 'zustand';
import { fetchExpenses, fetchExpenseSummary, fetchExpenseCategories } from '../../services/expenseApi.js';

export const useExpenseStore = create((set, get) => ({
  expenses: [],
  summary: null,
  categories: [
    
  ],
  loading: false,
  error: null,
  currentPage: 1,
  pageSize: 10,
  totalCount: 0,
  hasLoaded: false,
  lastFetched: null,

  setExpenses: (expenses) => set({ expenses }),
  setSummary: (summary) => set({ summary }),
  setCategories: (categories) => set({ categories }),
  addCategory: (category) => set((state) => ({ categories: [category, ...state.categories] })),
  updateCategory: (id, updatedCategory) => set((state) => ({
    categories: state.categories.map(cat => cat.id === id ? updatedCategory : cat)
  })),
  removeCategory: (id) => set((state) => ({ categories: state.categories.filter(cat => cat.id !== id) })),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setPageSize: (size) => set({ pageSize: size, currentPage: 1 }),

  // Load initial data
  loadExpenses: async (forceReload = false) => {
    const { hasLoaded, loading, lastFetched } = get();
    
    // Don't reload if already loaded and not forcing reload
    if (hasLoaded && !forceReload && Date.now() - lastFetched < 5 * 60 * 1000) {
      return;
    }

    if (loading) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const [expensesResponse, summaryResponse, categoriesResponse] = await Promise.all([
        fetchExpenses(),
        fetchExpenseSummary(),
        fetchExpenseCategories()
      ]);

      set({
        expenses: expensesResponse.expenses || [],
        summary: summaryResponse.summary || {
          totalExpenses: 0,
          count: 0,
          byStatus: {},
          byCategory: {}
        },
        categories: categoriesResponse.categories || [],
        hasLoaded: true,
        lastFetched: Date.now(),
        currentPage: 1,
        totalCount: (expensesResponse.expenses || []).length,
        loading: false,
        error: null
      });
    } catch (err) {
      set({
        error: err.message || 'Failed to load expenses',
        loading: false
      });
    }
  },

  // Get paginated expenses
  getPaginatedExpenses: (page = 1, pageSize = 10) => {
    const { expenses } = get();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return expenses.slice(startIndex, endIndex);
  },

  // Get filtered and paginated expenses
  getFilteredAndPaginated: (filters = {}, page = 1, pageSize = 10) => {
    const { expenses } = get();
    let filtered = expenses;

    if (filters.category && filters.category !== 'All') {
      filtered = filtered.filter(exp => exp.category_name === filters.category);
    }

    if (filters.sortBy === 'amount') {
      filtered.sort((a, b) => b.amount - a.amount);
    } else if (filters.sortBy === 'date') {
      filtered.sort((a, b) => new Date(b.expense_date) - new Date(a.expense_date));
    }

    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    return {
      data: filtered.slice(startIndex, endIndex),
      total: filtered.length,
      page,
      pageSize,
      pages: Math.ceil(filtered.length / pageSize)
    };
  },

  // Refresh all data
  refreshExpenses: async () => get().loadExpenses(true),

  // Add expense to store
  addExpense: (expense) => set((state) => ({
    expenses: [expense, ...state.expenses],
    totalCount: state.totalCount + 1
  })),

  // Update expense in store
  updateExpense: (id, updatedExpense) => set((state) => ({
    expenses: state.expenses.map(exp => exp.id === id ? updatedExpense : exp)
  })),

  // Remove expense from store
  removeExpense: (id) => set((state) => ({
    expenses: state.expenses.filter(exp => exp.id !== id),
    totalCount: state.totalCount - 1
  })),

  // Clear store
  clearExpenses: () => set({
    expenses: [],
    summary: null,
    categories: [],
    hasLoaded: false,
    currentPage: 1,
    totalCount: 0,
    lastFetched: null
  })
}));

export default useExpenseStore;
